import {
    IEditableEntity,
    IReadablePropEventBroker,
    EditableEntity,
    IPropEventBroker,
} from '@zdrbm/zdr-native-tools';
import { IContainerSelectorFactory } from './IContainerSelectorFactory';
import { PiError } from 'features/pi/model/pi-errors/PiError';

export type SelectorResult<T> = T extends IContainerSelectorFactory<infer U> ? U : never;

export interface IConversationRunContainer<TData = any> extends IEditableEntity {
    updateFrom(otherContainer: IConversationRunContainer<TData>): void;
    data: IReadablePropEventBroker<TData>;
    selectFromFactory<T extends IContainerSelectorFactory<any>>(
        selectorFactory: T,
    ): SelectorResult<T>;
    appendFromContainer(otherContainer: IConversationRunContainer<TData>): void;
}

export class ContainerTypeChecker implements IContainerSelectorFactory<string> {
    getScreenshotRequest(): string {
        return 'screenshot_request';
    }
    getText(): string {
        return 'text';
    }
    getUnknown(): string {
        return 'unknown';
    }
}

export abstract class ConversationRunContainer<TData>
    extends EditableEntity<keyof IConversationRunContainer<TData>>
    implements IConversationRunContainer<TData>
{
    data: IPropEventBroker<TData>;
    private containerTypeChecker: ContainerTypeChecker;

    constructor(id: string, data: TData) {
        super(id);
        this.data = this.createPropEventBroker('data', data);
        this.containerTypeChecker = new ContainerTypeChecker();
    }

    appendFromContainer(otherContainer: IConversationRunContainer<TData>): void {
        const isSameType = this.isSameTypeContainer(otherContainer);
        if (isSameType) {
            this.appendData(otherContainer.data.get() as TData);
        } else {
            console.error(new PiError('Trying to append data from different type of container'));
        }
    }

    protected appendData(_: TData): void {
        throw new PiError(
            `This container '${this.getEntityName()}' does not support appending data`,
        );
    }

    abstract selectFromFactory<T extends IContainerSelectorFactory<any>>(
        selectorFactory: T,
    ): SelectorResult<T>;

    isSameTypeContainer(otherContainer: IConversationRunContainer<any>): boolean {
        const thisType = this.selectFromFactory(this.containerTypeChecker);
        const otherType = otherContainer.selectFromFactory(this.containerTypeChecker);
        return thisType === otherType;
    }

    updateFrom(otherContainer: IConversationRunContainer<TData>): void {
        this.data.set(otherContainer.data.get());
    }
}
